import { projectsId, type ProjectsId } from "./Projects";
import { environmentsId, type EnvironmentsId } from "./Environments";
import { templateContentsId, type TemplateContentsId } from "./TemplateContents";
import { recipientType, type default as RecipientType } from "./RecipientType";
import { notificationStatus, type default as NotificationStatus } from "./NotificationStatus";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for notification_log_aggregations */
export type NotificationLogAggregationsId = string & { __flavor: "NotificationLogAggregationsId" };

/**
 * Represents the table public.notification_log_aggregations
 * @uniqueConstraints {"notification_log_aggregations_pkey":{"name":"notification_log_aggregations_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"notification_log_aggregations_project_id_idx":{"name":"notification_log_aggregations_project_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"notification_log_aggregations_environment_id_idx":{"name":"notification_log_aggregations_environment_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"environment_id","definition":"environment_id","predicate":null}]},"notification_log_aggregations_date_idx":{"name":"notification_log_aggregations_date_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"date","definition":"date","predicate":null}]},"notification_log_aggregations_template_content_id_idx":{"name":"notification_log_aggregations_template_content_id_idx","isPrimary":false,"isUnique":false,"columns":[{"name":"template_content_id","definition":"template_content_id","predicate":null}]},"notification_log_aggregations_unique_idx":{"name":"notification_log_aggregations_unique_idx","isPrimary":false,"isUnique":true,"columns":[{"name":"project_id","definition":"project_id","predicate":null},{"name":"environment_id","definition":"environment_id","predicate":null},{"name":"template_content_id","definition":"template_content_id","predicate":null},{"name":"date","definition":"date","predicate":null},{"name":"recipient_type","definition":"recipient_type","predicate":null},{"name":"status","definition":"status","predicate":null}]}}
 */
export default interface NotificationLogAggregationsTable {
  id: ColumnType<
    NotificationLogAggregationsId,
    NotificationLogAggregationsId | undefined,
    NotificationLogAggregationsId
  >;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  environmentId: ColumnType<EnvironmentsId, EnvironmentsId, EnvironmentsId>;

  templateContentId: ColumnType<
    TemplateContentsId | null,
    TemplateContentsId | null,
    TemplateContentsId | null
  >;

  date: ColumnType<Date, Date | string, Date | string>;

  recipientType: ColumnType<RecipientType, RecipientType, RecipientType>;

  status: ColumnType<NotificationStatus, NotificationStatus, NotificationStatus>;

  count: ColumnType<number, number | undefined, number>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;
}

export type NotificationLogAggregations = Selectable<NotificationLogAggregationsTable>;

export type NewNotificationLogAggregations = Insertable<NotificationLogAggregationsTable>;

export type NotificationLogAggregationsUpdate = Updateable<NotificationLogAggregationsTable>;

export const notificationLogAggregationsId =
  z.string() as unknown as z.Schema<NotificationLogAggregationsId>;

export const notificationLogAggregations = z.object({
  id: notificationLogAggregationsId,
  projectId: projectsId,
  environmentId: environmentsId,
  templateContentId: templateContentsId.nullable(),
  date: z.date(),
  recipientType: recipientType,
  status: notificationStatus,
  count: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const notificationLogAggregationsInitializer = z.object({
  id: notificationLogAggregationsId.optional(),
  projectId: projectsId,
  environmentId: environmentsId,
  templateContentId: templateContentsId.optional().nullable(),
  date: z.date(),
  recipientType: recipientType,
  status: notificationStatus,
  count: z.number().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const notificationLogAggregationsMutator = z.object({
  id: notificationLogAggregationsId.optional(),
  projectId: projectsId.optional(),
  environmentId: environmentsId.optional(),
  templateContentId: templateContentsId.optional().nullable(),
  date: z.date().optional(),
  recipientType: recipientType.optional(),
  status: notificationStatus.optional(),
  count: z.number().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// ── constraints for NotificationLogAggregations ─────────────────────────────────
export const NotificationLogAggregationsConstraints = {
  notification_log_aggregations_pkey: {
    name: "notification_log_aggregations_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  notification_log_aggregations_project_id_idx: {
    name: "notification_log_aggregations_project_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  notification_log_aggregations_environment_id_idx: {
    name: "notification_log_aggregations_environment_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "environment_id", definition: "environment_id", predicate: null }],
  },
  notification_log_aggregations_date_idx: {
    name: "notification_log_aggregations_date_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "date", definition: "date", predicate: null }],
  },
  notification_log_aggregations_template_content_id_idx: {
    name: "notification_log_aggregations_template_content_id_idx",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "template_content_id", definition: "template_content_id", predicate: null }],
  },
  notification_log_aggregations_unique_idx: {
    name: "notification_log_aggregations_unique_idx",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "project_id", definition: "project_id", predicate: null },
      { name: "environment_id", definition: "environment_id", predicate: null },
      { name: "template_content_id", definition: "template_content_id", predicate: null },
      { name: "date", definition: "date", predicate: null },
      { name: "recipient_type", definition: "recipient_type", predicate: null },
      { name: "status", definition: "status", predicate: null },
    ],
  },
} as const;
export type NotificationLogAggregationsConstraintsType =
  typeof NotificationLogAggregationsConstraints;
